import { useCallback, useEffect, useRef, useState } from 'react';
import { computePosition } from '@floating-ui/dom';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
  RangeSelection,
} from 'lexical';
import { createPortal } from 'react-dom';

import { IconButton } from '../components/IconButton';
import { useClickOutside } from '../hooks/useClickOutside';
import { $getSharedLinkTarget } from '../utils/$getSharedLinkTarget';

type EditLinkMenuPosition = { x: number; y: number } | undefined;

export const TOGGLE_EDIT_LINK_MENU: LexicalCommand<undefined> =
  createCommand('TOGGLE_EDIT_LINK_MENU');

export function EditLinkPlugin() {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [pos, setPos] = useState<EditLinkMenuPosition>(undefined);
  const [domRange, setDomRange] = useState<Range | undefined>(undefined);
  const [hasLink, setHasLink] = useState(false);

  const [editor] = useLexicalComposerContext();

  const resetState = useCallback(() => {
    setValue('');
    setError(false);
    setPos(undefined);
    setDomRange(undefined);
  }, []);

  useEffect(() => {
    return editor.registerCommand<undefined>(
      TOGGLE_EDIT_LINK_MENU,
      () => {
        const nativeSel = window.getSelection();
        const isCollapsed = nativeSel?.rangeCount === 0 || nativeSel?.isCollapsed;

        if (!!pos?.x || !!pos?.y || !ref.current || !nativeSel || isCollapsed) {
          resetState();
          return false;
        }

        const domRange = nativeSel.getRangeAt(0);

        computePosition(domRange, ref.current, { placement: 'bottom' })
          .then((pos) => {
            setPos({ x: pos.x, y: pos.y + 10 });
            setDomRange(domRange);
            editor.getEditorState().read(() => {
              const selection = $getSelection();
              if (selection && 'getNodes' in selection) {
                const linkTarget = $getSharedLinkTarget(selection as RangeSelection);
                setHasLink(!!linkTarget);
              }
            });
          })
          .catch(() => {
            resetState();
          });

        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, pos, resetState]);

  useEffect(() => {
    if (pos?.x && pos?.y) {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (selection && 'getNodes' in selection) {
          const initialUrl = $getSharedLinkTarget(selection as RangeSelection) ?? '';
          setValue(initialUrl);
        }
      });
      inputRef.current?.focus();
    }
  }, [pos, editor]);

  useClickOutside(ref, resetState);

  const handleSetLink = () => {
    if (!value) return;

    const isLinkSet = editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
      url: value,
      target: '_blank',
    });

    if (isLinkSet) resetState();
    else setError(true);
  };

  const handleRemoveLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    resetState();
  };

  return (
    <>
      <FakeSelection range={domRange} />
      <div
        ref={ref}
        style={{ top: pos?.y, left: pos?.x }}
        aria-hidden={!pos?.x || !pos?.y}
        className={`absolute flex items-center justify-between border-[1px] bg-blue-gray-100 ${
          error ? 'border-red-600' : 'border-blue-gray-300'
        } gap-1 rounded-md p-1 ${pos?.x && pos.y ? 'opacity-1 visible' : 'invisible opacity-0'}`}
      >
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="border-none bg-transparent px-2 py-1 text-xs outline-none"
          placeholder="Enter URL"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSetLink();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              resetState();
            }
          }}
        />
        {hasLink ? <IconButton icon="trash" onClick={handleRemoveLink} /> : null}
        <IconButton icon="check" disabled={!value} onClick={handleSetLink} />
      </div>
    </>
  );
}

function FakeSelection({ range }: { range: Range | undefined }) {
  if (!range) return null;

  const domRect = range.getBoundingClientRect();
  return createPortal(
    <div
      className="absolute -z-[1] bg-blue-gray-200"
      style={{
        left: domRect.left,
        width: domRect.width,
        top: domRect.top - 2,
        height: domRect.height + 4,
      }}
    />,
    document.body
  );
}
